import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/feature/Navigation';
import Footer from '@/pages/home/components/Footer';
import SEOHead from '@/components/SEOHead';

const supervisionOptions = [
  {
    id: 'third-party',
    badge: 'Option 01',
    icon: 'ri-shield-user-line',
    title: 'Independent Third-Party Supervisor',
    tagline: 'Someone in your corner who answers only to you.',
    description:
      'This is the gold standard of medical oversight. We assign you a fully independent dental specialist — someone who has zero financial relationship with your treating clinic. Their only job is to protect your interests, review your treatment plan, and make sure everything is done correctly.',
    why: 'Most dental tourism patients have no idea if the treatment they received was actually necessary or correctly done. Our independent supervisor closes that gap completely.',
    features: [
      { icon: 'ri-shield-check-line', text: 'Zero conflict of interest — supervisor is paid by you, not the clinic' },
      { icon: 'ri-file-list-3-line', text: 'Full review of your treatment plan before any work begins' },
      { icon: 'ri-eye-line', text: 'On-site presence during key treatment stages' },
      { icon: 'ri-camera-line', text: 'Complete documentation: X-rays, photos, clinical notes — all yours' },
      { icon: 'ri-message-3-line', text: 'Direct line to your supervisor throughout your stay' },
    ],
    bestFor: 'First-time dental tourists, complex multi-stage treatments, implants, full smile makeovers',
    image: 'https://public.readdy.ai/ai/img_res/58e193d369b8c5e39e0aa471a66e97eb.jpg',
  },
  {
    id: 'own-doctor',
    badge: 'Option 02',
    icon: 'ri-user-heart-line',
    title: 'Your Own Dentist Stays Involved',
    tagline: 'The dentist you already trust stays part of your journey.',
    description:
      'You already have a dentist back home who knows your history. Why cut them out? With this option, we create a direct communication bridge between your local dentist and the treating clinic in Istanbul. Your dentist reviews the plan, stays updated, and handles your aftercare when you return.',
    why: 'Continuity of care is one of the biggest concerns in dental tourism. This option solves it by keeping your existing dental relationship intact.',
    features: [
      { icon: 'ri-links-line', text: 'Structured communication protocol between Istanbul clinic and your dentist' },
      { icon: 'ri-file-transfer-line', text: 'Full treatment records, X-rays, and reports sent to your dentist' },
      { icon: 'ri-video-chat-line', text: 'Video consultation between your dentist and the Istanbul specialist' },
      { icon: 'ri-calendar-check-line', text: 'Aftercare plan designed for seamless handoff when you return home' },
      { icon: 'ri-heart-pulse-line', text: 'Your dentist handles follow-ups — no gaps in your care' },
    ],
    bestFor: 'Patients with established dental relationships, ongoing treatment plans, those who prefer familiar aftercare',
    image: 'https://readdy.ai/api/search-image?query=two%20dentists%20having%20a%20professional%20video%20consultation%20on%20laptop%20modern%20clinic%20dark%20background%20warm%20lighting%20premium%20medical%20setting%20editorial%20photography%20cinematic%20style&width=700&height=520&seq=sup-opt2&orientation=landscape',
  },
  {
    id: 'our-expert',
    badge: 'Option 03',
    icon: 'ri-team-line',
    title: 'Our Senior Expert Supervisor',
    tagline: 'A specialist with 15+ years watches over every step.',
    description:
      'Don\'t have a local dentist? Prefer to rely on our network? We assign you one of our senior dental specialists — a highly experienced clinician with 15+ years in the field who personally oversees your case from start to finish. They\'re your advocate, your second opinion, and your quality guarantee.',
    why: 'Experience matters. Our senior supervisors have seen thousands of cases and know exactly what to look for — and what to question.',
    features: [
      { icon: 'ri-award-line', text: '15+ years of clinical experience in complex dental cases' },
      { icon: 'ri-stethoscope-line', text: 'Personal case review and treatment plan approval before you arrive' },
      { icon: 'ri-user-star-line', text: 'Dedicated point of contact throughout your entire journey' },
      { icon: 'ri-check-double-line', text: 'Final quality sign-off before you leave Istanbul' },
      { icon: 'ri-phone-line', text: 'Available for post-treatment consultations for 6 months' },
    ],
    bestFor: 'Patients without a local dentist, those seeking maximum peace of mind, complex reconstructive cases',
    image: 'https://readdy.ai/api/search-image?query=senior%20experienced%20dental%20specialist%20in%20white%20coat%20confident%20professional%20portrait%20dark%20moody%20studio%20lighting%20premium%20medical%20photography%20editorial%20style%20warm%20amber%20tones%20Istanbul%20Turkey&width=700&height=520&seq=sup-opt3&orientation=landscape',
  },
];

const pillars = [
  {
    icon: 'ri-shield-check-line',
    title: 'Zero Conflict of Interest',
    desc: 'Your supervisor is never paid by the clinic. Their loyalty is entirely to you — not to the treatment revenue.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Full Records Ownership',
    desc: 'Every scan, X-ray, photo, and clinical note belongs to you. We send everything to you and your home dentist.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Continuity of Care',
    desc: 'We design your aftercare plan before you even arrive, ensuring a seamless handoff when you return home.',
  },
  {
    icon: 'ri-time-line',
    title: 'Real-Time Oversight',
    desc: 'Supervision happens at every stage — not just a final check. We\'re present when it matters most.',
  },
  {
    icon: 'ri-translate-2',
    title: 'Language Barrier Removed',
    desc: 'Your supervisor communicates in your language and translates everything — no medical jargon, no confusion.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Revision Guarantee',
    desc: 'If something isn\'t right, we coordinate a revision. You\'re never left dealing with problems alone.',
  },
];

const faqs = [
  {
    q: 'Do I have to pay extra for supervision?',
    a: 'Supervision is included in our service fee — it\'s not an add-on. It\'s the core of what makes Plexus Alliance different from booking a clinic directly.',
  },
  {
    q: 'Can the supervisor actually stop a treatment if something is wrong?',
    a: 'Yes. Our supervisors have the authority to pause or flag any treatment they believe is not in your best interest. This has happened, and it\'s exactly why this layer exists.',
  },
  {
    q: 'What if I already have a dentist I trust back home?',
    a: 'Perfect — choose Option 02. We\'ll build a direct communication bridge between your dentist and the Istanbul clinic so your care is fully continuous.',
  },
  {
    q: 'How is the supervisor different from the treating dentist?',
    a: 'The treating dentist performs the work. The supervisor reviews, questions, and approves the plan — independently. They have no financial stake in what treatment you receive.',
  },
  {
    q: 'What happens after I go home?',
    a: 'Your supervisor remains available for follow-up consultations for up to 6 months. If a revision is needed, we coordinate it — usually at no extra cost.',
  },
  {
    q: 'Is supervision available for all treatments?',
    a: 'Yes. Whether you\'re getting a single implant or a full smile makeover, supervision is available and recommended for every case we handle.',
  },
];

export default function SupervisionPage() {
  const [activeOption, setActiveOption] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || '';

  const active = supervisionOptions[activeOption];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/supervision`,
      url: `${siteUrl}/supervision`,
      name: 'Medical Supervision | Plexus Alliance — Protected Dental Care in Istanbul',
      description: 'Plexus Alliance provides independent medical supervision for every dental treatment in Istanbul, Turkey. Three supervision options to protect your health and investment.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Supervision', item: `${siteUrl}/supervision` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Independent Medical Supervision for Dental Tourism',
      description: 'An independent dental specialist supervises your entire treatment journey in Istanbul, Turkey — reviewing your plan, monitoring progress, and protecting your interests.',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'Plexus Alliance',
        url: siteUrl,
      },
      serviceType: 'Medical Supervision',
      areaServed: { '@type': 'Country', name: 'Worldwide' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Supervision Options',
        itemListElement: supervisionOptions.map((opt, i) => ({
          '@type': 'Offer',
          position: i + 1,
          name: opt.title,
          description: opt.description,
        })),
      },
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Medical Supervision | Plexus Alliance — Protected Dental Care in Istanbul"
        description="Plexus Alliance provides independent medical supervision for every dental treatment in Istanbul, Turkey. Three supervision options to protect your health and investment."
        keywords="dental supervision Turkey, independent medical supervisor Istanbul, supervised dental tourism, dental oversight abroad"
        canonical={`${siteUrl}/supervision`}
        schema={schema}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=luxury%20private%20medical%20clinic%20interior%20Istanbul%20Turkey%20elegant%20dark%20corridor%20warm%20amber%20lighting%20marble%20floors%20sophisticated%20architecture%20premium%20healthcare%20environment%20cinematic%20wide%20angle&width=1400&height=700&seq=sup-hero&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0a0a0a]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-px bg-amber-400/50" />
            <span
              className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Your Protection
            </span>
            <div className="w-10 h-px bg-amber-400/50" />
          </div>
          <h1
            className="text-4xl md:text-7xl font-normal text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            Medical Supervision
          </h1>
          <p
            className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            The one thing most dental tourism companies don't offer — and the one thing that makes all the difference. An independent expert watching over your treatment, answering only to you.
          </p>
          {/* Option pills */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {supervisionOptions.map((opt, i) => (
              <button
                key={opt.id}
                onClick={() => setActiveOption(i)}
                className={`flex items-center gap-2 px-5 py-2.5 border text-xs tracking-widest uppercase transition-all duration-300 cursor-pointer rounded-full whitespace-nowrap ${
                  activeOption === i
                    ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                    : 'border-white/10 hover:border-amber-400/40 text-white/40 hover:text-amber-400'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <i className={`${opt.icon} text-sm`}></i>
                {opt.badge}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Why Supervision Matters */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-px bg-amber-400/50" />
                <span
                  className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Why It Matters
                </span>
              </div>
              <h2
                className="text-3xl md:text-5xl font-normal text-white leading-tight mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Most clinics work for themselves. We work for you.
              </h2>
              <p
                className="text-white/45 text-base leading-relaxed mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                When you book a clinic directly, the only person reviewing your treatment plan is the person who profits from it. There's no independent check. No second opinion. No one asking "is this really necessary?"
              </p>
              <p
                className="text-white/45 text-base leading-relaxed mb-10"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Plexus Alliance changes that. Every patient gets an independent supervisor — a qualified dental specialist whose only job is to protect your health and your money. They review your plan, question anything that doesn't add up, and stay with you from first consultation to final check.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/book-treatment"
                  className="flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Get Supervised Care
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/how-it-works"
                  className="flex items-center justify-center gap-3 border border-white/15 hover:border-amber-400/40 text-white/50 hover:text-amber-400 px-8 py-4 font-medium text-sm tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  See How It Works
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden" style={{ height: '460px' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=dental%20specialist%20reviewing%20patient%20xray%20documents%20in%20dark%20luxury%20clinic%20Istanbul%20Turkey%20warm%20amber%20lighting%20professional%20medical%20consultation%20premium%20editorial%20photography%20cinematic%20style&width=700&height=520&seq=sup-why&orientation=landscape"
                  alt="Medical supervision"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-3xl font-normal text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>100%</p>
                      <p className="text-white/40 text-xs tracking-widest uppercase mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Cases Supervised</p>
                    </div>
                    <div className="w-px h-12 bg-white/10" />
                    <div className="text-center">
                      <p className="text-3xl font-normal text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>15+</p>
                      <p className="text-white/40 text-xs tracking-widest uppercase mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Years Experience</p>
                    </div>
                    <div className="w-px h-12 bg-white/10" />
                    <div className="text-center">
                      <p className="text-3xl font-normal text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>3</p>
                      <p className="text-white/40 text-xs tracking-widest uppercase mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>Options Available</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-amber-400/20" />
              <div className="absolute -top-3 -left-3 w-16 h-16 border border-amber-400/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Three Options — Tab Switcher */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="w-8 h-px bg-amber-400/40" />
              <span
                className="text-amber-400/70 text-xs tracking-[0.25em] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Choose Your Model
              </span>
              <div className="w-8 h-px bg-amber-400/40" />
            </div>
            <h2
              className="text-3xl md:text-5xl font-normal text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Three ways to stay protected
            </h2>
          </div>

          {/* Tab buttons */}
          <div className="flex flex-wrap gap-0 mb-10 border border-white/10">
            {supervisionOptions.map((opt, i) => (
              <button
                key={opt.id}
                onClick={() => setActiveOption(i)}
                className={`flex-1 px-6 py-4 text-xs font-medium tracking-widest uppercase transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  activeOption === i
                    ? 'bg-amber-400 text-black'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <i className={`${opt.icon} text-sm`}></i>
                {opt.badge}
              </button>
            ))}
          </div>

          {/* Active option card */}
          <div className="grid md:grid-cols-2 gap-0 border border-white/8 overflow-hidden">
            {/* Image */}
            <div className="relative overflow-hidden" style={{ minHeight: '460px' }}>
              <img
                src={active.image}
                alt={active.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p
                  className="text-amber-400/60 text-xs tracking-widest uppercase mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Best for
                </p>
                <p
                  className="text-white/70 text-sm leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {active.bestFor}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="bg-[#0f0f0f] p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 border border-amber-400/25 rounded-lg">
                  <i className={`${active.icon} text-lg text-amber-400`}></i>
                </div>
                <span
                  className="text-white/30 text-xs tracking-[0.2em] uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {active.badge}
                </span>
              </div>

              <h3
                className="text-2xl md:text-3xl font-normal text-white mb-3 leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {active.title}
              </h3>
              <p
                className="text-amber-400 text-sm font-medium mb-5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {active.tagline}
              </p>
              <p
                className="text-white/45 text-sm leading-relaxed mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {active.description}
              </p>

              {/* Why callout */}
              <div className="border-l-2 border-amber-400/40 pl-4 mb-8">
                <p
                  className="text-white/35 text-sm italic leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {active.why}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {active.features.map((feat, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className={`${feat.icon} text-amber-400/70 text-base`}></i>
                    </div>
                    <span
                      className="text-white/55 text-sm leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {feat.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to={`/book-treatment?supervision=${active.id}`}
                className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap self-start"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Choose This Option
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Six Pillars */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="w-8 h-px bg-amber-400/40" />
              <span
                className="text-amber-400/70 text-xs tracking-[0.25em] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Our Commitments
              </span>
              <div className="w-8 h-px bg-amber-400/40" />
            </div>
            <h2
              className="text-3xl md:text-5xl font-normal text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What every supervised patient gets
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {pillars.map((pillar, i) => (
              <div
                key={i}
                className="bg-[#0d0d0d] p-8 group hover:bg-[#131313] transition-colors duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <i className={`${pillar.icon} text-2xl text-amber-400/50 group-hover:text-amber-400 transition-colors duration-300`}></i>
                </div>
                <h4
                  className="text-base font-medium text-white/80 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {pillar.title}
                </h4>
                <p
                  className="text-white/35 text-sm leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison: With vs Without */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-5xl font-normal text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              With supervision vs. without
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/5">
            {/* Without */}
            <div className="bg-[#0f0f0f] p-8 md:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5">
                  <i className="ri-close-line text-white/30 text-lg"></i>
                </div>
                <h3
                  className="text-lg font-medium text-white/40"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Without Supervision
                </h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Clinic reviews its own treatment plan',
                  'No independent second opinion',
                  'Records may not be shared with you',
                  'Language barrier with no mediator',
                  'Aftercare left to chance',
                  'Revisions are your problem to solve',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-white/20 text-base mt-0.5 flex-shrink-0"></i>
                    <span
                      className="text-white/30 text-sm leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* With */}
            <div className="bg-[#111111] p-8 md:p-10 border border-amber-400/15">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-400/10">
                  <i className="ri-shield-check-line text-amber-400 text-lg"></i>
                </div>
                <h3
                  className="text-lg font-medium text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  With Plexus Alliance Supervision
                </h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Independent specialist reviews your plan',
                  'Second opinion built into the process',
                  'All records belong to you — always',
                  'Dedicated coordinator speaks your language',
                  'Structured aftercare plan from day one',
                  'Revisions coordinated at no extra cost',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-amber-400/70 text-base mt-0.5 flex-shrink-0"></i>
                    <span
                      className="text-white/65 text-sm leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="w-8 h-px bg-amber-400/40" />
              <span
                className="text-amber-400/70 text-xs tracking-[0.25em] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Common Questions
              </span>
              <div className="w-8 h-px bg-amber-400/40" />
            </div>
            <h2
              className="text-3xl md:text-5xl font-normal text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Questions about supervision
            </h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-white/8 overflow-hidden hover:border-amber-400/20 transition-colors duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
                >
                  <span
                    className="text-white/80 text-base font-medium group-hover:text-white transition-colors duration-200 pr-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.q}
                  </span>
                  <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border border-white/10 group-hover:border-amber-400/30 transition-colors duration-200">
                    <i
                      className={`text-sm text-white/40 group-hover:text-amber-400 transition-all duration-300 ${
                        openFaq === i ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-48' : 'max-h-0'
                  }`}
                >
                  <p
                    className="px-6 pb-5 text-white/40 text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20Bosphorus%20bridge%20night%20lights%20reflection%20water%20dark%20moody%20cinematic%20premium%20travel%20photography%20wide%20angle%20editorial%20style%20golden%20hour&width=1400&height=600&seq=sup-cta&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-black/90" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-8 h-px bg-amber-400/40" />
            <span
              className="text-amber-400/70 text-xs tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Start Protected
            </span>
            <div className="w-8 h-px bg-amber-400/40" />
          </div>
          <h2
            className="text-3xl md:text-6xl font-normal text-white mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your health deserves an independent voice.
          </h2>
          <p
            className="text-white/45 text-lg mb-10 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Every Plexus Alliance patient gets supervised care. Tell us what you need — we'll match you with the right option.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book-treatment"
              className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book With Supervision
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/treatments"
              className="flex items-center gap-3 border border-white/20 hover:border-amber-400/40 text-white/60 hover:text-amber-400 px-10 py-4 font-medium text-sm tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Browse Treatments
            </Link>
          </div>
        </div>
      </section>

      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
